<template>
    <div>
        <label class="headline-section pb-3 pt-3">{{ $t("Current password") }}</label>
        <div class="input-container">
            <input :type="hiddenCurrent ? 'password' : 'text'" :disabled="busy" v-model="currentPass" class="form-control" />
            <button @click="togglePasswordVisibility('current')" class="toggle-btn ms-3" @input="markDirty">
                <IconItem v-if="hiddenCurrent" icon="mdi:eye-off" />
                <IconItem v-else icon="mdi:eye" />
            </button>
        </div>
        <label class="headline-section pb-3 pt-3">{{ $t("New password") }}</label>
        <div class="input-container">
            <input
                :type="hiddenNew ? 'password' : 'text'"
                v-model="newPass"
                :disabled="busy"
                @input="markDirty"
                class="form-control"
                :class="{ 'form-control': true, 'is-invalid': hasError }"
            />
            <button @click="togglePasswordVisibility('new')" class="toggle-btn ms-3">
                <IconItem v-if="hiddenNew" icon="mdi:eye-off" />
                <IconItem v-else icon="mdi:eye" />
            </button>
        </div>
        <label class="headline-section pb-3 pt-3">{{ $t("Confirm password") }}</label>
        <div class="input-container">
            <input
                :type="hiddenConfirm ? 'password' : 'text'"
                :disabled="busy"
                v-model="confirmPass"
                @input="markDirty"
                class="form-control"
                :class="{ 'form-control': true, 'is-invalid': hasError }"
            />
            <button @click="togglePasswordVisibility('confirm')" class="toggle-btn ms-3">
                <IconItem v-if="hiddenConfirm" icon="mdi:eye-off" />
                <IconItem v-else icon="mdi:eye" />
            </button>
        </div>
        <div v-if="error" class="error-message">{{ error }}</div>
        <div class="btn-bottom-modal d-flex justify-content-end align-items-center mt-3">
            <button type="button" class="btn btn-primary btn-mr" :disabled="busy || !dirty" @click="changePassword">
                <IconItem icon="mdi:lock" /><span class="text-responsive">{{ $t("Change password") }}</span>
            </button>
        </div>
    </div>
</template>

<script lang="ts">
import { useVModel } from "@/utils/v-model";
import { defineComponent } from "vue";
import { Request } from "@asanrom/request-browser";
import { ApiAccount } from "@/api/api-group-account";
import { getUniqueStringId } from "@/utils/unique-id";

export default defineComponent({
    name: "PasswordPage",
    setup(props) {
        return {
            displayStatus: useVModel(props, "display"),
            loadRequestId: getUniqueStringId(),
        };
    },
    data: function () {
        return {
            currentPass: "",
            newPass: "",
            confirmPass: "",
            busy: false,
            error: "",
            hasError: false,
            hiddenCurrent: true,
            hiddenNew: true,
            hiddenConfirm: true,
            dirty: false,
        };
    },
    methods: {
        togglePasswordVisibility(field: string) {
            if (field === "current") {
                this.hiddenCurrent = !this.hiddenCurrent;
            } else if (field === "new") {
                this.hiddenNew = !this.hiddenNew;
            } else if (field === "confirm") {
                this.hiddenConfirm = !this.hiddenConfirm;
            }
        },
        changePassword: function () {
            if (this.busy) {
                return;
            }

            if (this.newPass !== this.confirmPass) {
                this.hasError = true;
                this.error = this.$t("Passwords do not match");
                return;
            }

            this.error = "";
            this.hasError = false;
            this.busy = true;

            Request.Pending(this.loadRequestId, ApiAccount.ChangePassword({ old_password: this.currentPass, new_password: this.newPass }))

                .onSuccess(() => {
                    this.busy = false;
                    this.dirty = false;
                    this.$showSnackBar(this.$t("Password updated"));
                    this.currentPass = "";
                    this.newPass = "";
                    this.confirmPass = "";
                })
                .onCancel(() => {
                    this.busy = false;
                })
                .onRequestError((err, handleErr) => {
                    this.busy = false;
                    this.hasError = true;
                    handleErr(err, {
                        unauthorized: () => {
                            this.$requireLogin();
                        },
                        badRequestWeakPassword: () => {
                            this.error = this.$t("Password is too weak");
                        },
                        badRequestWrongPassword: () => {
                            this.error = this.$t("Wrong password");
                        },
                        badRequest: () => {
                            this.error = this.$t("Bad request");
                        },
                        serverError: () => {
                            this.error = this.$t("Internal server error");
                        },
                        networkError: () => {
                            this.error = this.$t("Could not connect to the server");
                        },
                    });
                })
                .onUnexpectedError((err) => {
                    this.error = err.message;
                    console.error(err);
                    this.busy = false;
                    this.hasError = true;
                });
        },
        markDirty: function () {
            this.dirty = true;
        },
    },
    mounted: function () {},
});
</script>

<style scoped>
@import "@/style/routes/configuration/user-config.css";
</style>
